import PropTypes from 'prop-types'
import { Box, Checkbox, Flex, Input, Link, Progress, Table, Tbody, Td, Tfoot, Th, Thead, Tr, Text, Select } from '@chakra-ui/react'
import { forwardRef, useCallback, useMemo } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { UiTableOrderByIcon, OrderByDirection } from './UiTableOrderByIcon'

// eslint-disable-next-line react/display-name
export const UiTable = forwardRef(({
  totalRecords,
  itemsPerPage,
  onSelectItemsPerPage,
  search,
  onSearch,
  rows,
  rowKey,
  columns,
  selected,
  allSelected,
  onSelect,
  isLoading,
  orderBy,
  onOrderBy,
  ...rest
}, ref) => {
  const canSelect = typeof onSelect === 'function'
  const getRowKey = useCallback((row) => String(row[rowKey]), [rowKey])
  const isSelected = useCallback(
    (row) => !!allSelected || selected?.some((r) => getRowKey(r) === getRowKey(row)),
    [allSelected, getRowKey, selected]
  )
  const isIndeterminate = useMemo(
    () => !allSelected && rows.some(isSelected) && !rows.every(isSelected),
    [allSelected, isSelected, rows]
  )
  const isChecked = useMemo(
    () => !!allSelected || rows.filter(isSelected).length === rows.length,
    [allSelected, isSelected, rows]
  )

  const handleOrderBy = (orderByKey) => {
    if (!orderByKey || !onOrderBy) return
    if (orderByKey === orderBy?.key) {
      return onOrderBy({
        key: orderByKey,
        direction:
          orderBy?.direction?.toLowerCase() === 'desc'
            ? OrderByDirection.Ascending
            : OrderByDirection.Descending
      })
    } else {
      return onOrderBy({
        key: orderByKey,
        direction: OrderByDirection.Ascending
      })
    }
  }

  const deboucedSearch = useDebouncedCallback(
    ({ target }) => onSearch(target.value),
    400
  )

  return (
    <>
      <Box position="relative">
        <Box ref={ref} {...rest}>
          <Flex justifyContent="space-between" alignItems="center" mb={2}>
            <Text fontSize="sm" pl={4}>{totalRecords} records found</Text>
            <Flex columnGap={2}>
              <Select size="sm" w="80px" value={itemsPerPage} onChange={onSelectItemsPerPage}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </Select>
              <Input size="sm" w="250px" defaultValue={search} onChange={deboucedSearch} placeholder="Search..." />
            </Flex>
          </Flex>
          <Progress
            size='xs'
            isIndeterminate
            opacity={isLoading ? 1 : 0}
          />
          <Table
            borderColor="gray.200"
            borderStyle="solid"
            borderWidth={1}
          >
            <Thead>
              <Tr bg="gray.300">
                {!!rows?.length && canSelect && (
                  <Th
                    borderRightColor="gray.200"
                    borderRightStyle="solid"
                    borderRightWidth={1}
                    textAlign="center"
                    w="30px"
                  >
                    <Checkbox
                      isChecked={isChecked}
                      isIndeterminate={isIndeterminate}
                      onChange={(e) => onSelect(e.target.checked)}
                      data-testid="th-checkbox"
                    />
                  </Th>
                )}
                {columns.map((column, idx) => {
                  const canColumnOrderBy = !!onOrderBy && !!column.orderBy
                  const isColumnOrderedBy = canColumnOrderBy && orderBy?.key === column.orderBy
                  return (
                    <Th
                      borderRightColor="gray.200"
                      borderRightStyle="solid"
                      borderRightWidth={1}
                      key={idx}
                      onClick={() => handleOrderBy(column.orderBy)}
                      style={{ ...column.style }}
                    >
                      <Flex
                        className="group"
                        as={canColumnOrderBy ? Link : undefined}
                        alignItems="center"
                        justifyContent="flex-start"
                        gap={1}
                        color={canColumnOrderBy ? 'atomicBlue.900' : 'atomicGray.900'}
                      >
                        <Text
                          fontWeight="inherit"
                          color="inherit"
                        >
                          {column.label}
                        </Text>
                        <UiTableOrderByIcon
                          canColumnOrderBy={canColumnOrderBy}
                          isColumnOrderedBy={isColumnOrderedBy}
                          orderBy={orderBy}
                        />
                      </Flex>
                    </Th>
                  )
                })}
              </Tr>
            </Thead>
            {!rows?.length ? (
              <Tfoot>
                <Tr>
                  <Td fontSize="sm" colSpan={99}>No items found&nbsp;</Td>
                </Tr>
              </Tfoot>
            ) : (
              <Tbody>
                {rows.map((row) => {
                  const key = getRowKey(row)
                  return (
                    <Tr
                      key={key}
                      className={isSelected(row) ? 'selected' : ''}
                    >
                      {canSelect && (
                        row.canSelect ? (
                          <Td
                            py={2}
                            borderRightColor="gray.200"
                            borderRightStyle="solid"
                            borderRightWidth={1}
                            textAlign="center"
                          >
                            <Checkbox
                              isChecked={isSelected(row)}
                              onChange={(e) => onSelect(e.target.checked, row)}
                              data-testid="tr-checkbox"
                            />
                          </Td>
                        ) : (
                          <Td
                            py={2}
                            borderRightColor="gray.200"
                            borderRightStyle="solid"
                            borderRightWidth={1}
                            textAlign="center">
                            </Td>
                        )
                      )}
                      {columns.map(({ render, style }, idx) => (
                        <Td
                          py={2}
                          fontSize="sm"
                          borderRightColor="gray.200"
                          borderRightStyle="solid"
                          borderRightWidth={1}
                          key={`${key}-${idx}`}
                          style={{ ...style }}
                        >
                          {render(row)}
                        </Td>
                      ))}
                    </Tr>
                  )
                })}
              </Tbody>
            )}
          </Table>
        </Box>
      </Box>
    </>
  )
})

UiTable.propTypes = {
  totalRecords: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  onSelectItemsPerPage: PropTypes.func,
  search: PropTypes.string,
  onSearch: PropTypes.func,
  /** the rows of data to populate the table */
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** a unique identifier property for each row */
  rowKey: PropTypes.string.isRequired,
  /** the columns used to build out the table */
  columns: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    orderBy: PropTypes.string,
    render: PropTypes.func.isRequired
  })),
  /** the selected items in the table */
  selected: PropTypes.arrayOf(PropTypes.object),
  /** makes all items in the table appear selected */
  allSelected: PropTypes.bool,
  /** callback when a checkbox is clicked, returns a row if a row checkbox clicked, returns no row on a select all click */
  onSelect: PropTypes.func,
  /** shows a loading progress bar */
  isLoading: PropTypes.bool,
  /** orderBy */
  orderBy: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.oneOf(Object.values(OrderByDirection))
  }),
  /** set orderBy for query */
  onOrderBy: PropTypes.func
}
